import React from "react";
import axios from "axios";
import GlobalError from "shared-anpost-components/components/globalError/GlobalError";
import { useHistory } from "react-router-dom";
import TransactionsGrid from "./click-and-post/TransactionsGrid";
import profileHooks from "../../../hooks/profileHooks";
import { getTransactions, getJWTInfo, getOrders, getShopOrders } from "../../../services/Api";
import { Box } from "./ui";
import { DmOrderList, ProductTabList, TransactionsHeader } from "./direct-mail/components/DmOrderList";
import { useSearchParams } from "./direct-mail/utils";
import ShopOrdersList from "./shop/ShopOrdersList";
// import { TransactionsData } from "./Transactions.config"; // for dummy data

const PRODUCTS = {
    CLICK_AND_POST: "click-and-post",
    DIRECT_MAIL: "direct-mail",
    SHOP: "shop"
};

const Transactions = () => {
    const history = useHistory();
    const { setSearchParam, searchParams } = useSearchParams();

    // TODO: this is wrong. Hooks should start with keyword "use" otherwise it might not work as expected
    const { values } = profileHooks();

    const { ciamUserId: userId } = values;
    const selectedProduct = searchParams.find(p => p?.[0] === "product")?.[1];

    const cNpOrders = useCnPOrdersQuery({ enabled: selectedProduct === PRODUCTS.CLICK_AND_POST, userId });
    const dmOrders = useDmOrdersQuery({ enabled: selectedProduct === PRODUCTS.DIRECT_MAIL, userId });
    const shopOrders = useShopOrdersQuery({ enabled: selectedProduct === PRODUCTS.SHOP, userId });

    // initialize with CLICK_AND_POST product
    React.useEffect(() => {
        if (!selectedProduct) onSelectProduct(PRODUCTS.CLICK_AND_POST);
    }, []);

    const onSelectProduct = async value => {
        if (!values.loggedIn || !values.ciamUserId) return;
        setSearchParam("product", value);
    };

    const error = cNpOrders.error || dmOrders.error || shopOrders.error;

    const onReset = () => {
        cNpOrders.reset();
        dmOrders.reset();
        shopOrders.reset();
    };

    return (
        <>
            {error && <GlobalError closeFunc={onReset} />}
            <Box gap="32px">
                <TransactionsHeader />
                <ProductTabList onSelect={onSelectProduct} selected={selectedProduct} />
                {selectedProduct === PRODUCTS.CLICK_AND_POST && <TransactionsGrid loader={cNpOrders.isPending} items={cNpOrders.data || []} history={history} infoBtn price></TransactionsGrid>}
                {selectedProduct === PRODUCTS.DIRECT_MAIL && (
                    <DmOrderList
                        error={dmOrders.error}
                        totalPages={dmOrders.totalPages}
                        totalRecords={dmOrders.totalRecords}
                        pageSize={dmOrders.pageSize}
                        onPageChange={dmOrders.onPageChange}
                        pageNumber={dmOrders.pageNumber}
                        isLoading={dmOrders.isLoading}
                        items={dmOrders.data}
                    />
                )}
                {selectedProduct === PRODUCTS.SHOP && 
                    <ShopOrdersList 
                        error={shopOrders.error}
                        currentPage={shopOrders.currentPage}
                        ordersPerPage={shopOrders.ordersPerPage}
                        ordersPerPageChange={shopOrders.ordersPerPageChange}
                        onPageChange={shopOrders.onPageChange}
                        items={shopOrders.data}
                        totalItems={shopOrders.totalItems}
                        isLoading={shopOrders.isLoading}
                    />
                }
            </Box>
        </>
    );
};

export default Transactions;

let cachedCnPOrders;
function useCnPOrdersQuery({ enabled = true, userId } = {}) {
    const [data, setData] = React.useState(cachedCnPOrders);
    const [error, setError] = React.useState(undefined);
    const [isLoading, setLoading] = React.useState(true);
    const createToken = useCancelToken();

    const fetch = async () => {
        setLoading(true);
        setError(undefined);
        const cancelToken = createToken();
        try {
            getJWTInfo(userId);
            const response = await getTransactions(userId, { cancelToken });
            if (response?.data?.baskets) {
                cachedCnPOrders = response.data.baskets;
                setData(cachedCnPOrders);
            }
        } catch (e) {
            if (axios.isCancel(error)) {
                console.log("Request canceled.", error.message);
                cachedCnPOrders = undefined;
                setData(cachedCnPOrders);
                setError(undefined);
            } else {
                const hasNoBasket = e?.response?.data?.basketsCount === 0;
                if (hasNoBasket) return;
                cachedCnPOrders = undefined;
                setData(cachedCnPOrders);
                setError(e?.message || "Failed to get cnp orders.");
            }
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setLoading(false);
        cachedCnPOrders = undefined;
        setData(cachedCnPOrders);
        setError(undefined);
    };

    React.useEffect(() => {
        if (enabled) fetch();
    }, [enabled]);

    const isPending = !!(isLoading && !data);

    return {
        data,
        isLoading,
        fetch,
        error,
        isPending,
        reset
    };
}

let cachedDmOrders;
function useDmOrdersQuery({ enabled = true, userId } = {}) {
    const pageSize = 10;
    const [data, setData] = React.useState(cachedDmOrders);
    const [error, setError] = React.useState(undefined);
    const [isLoading, setLoading] = React.useState(true);
    const [totalPages, setTotalPages] = React.useState(0);
    const [totalRecords, setTotalRecords] = React.useState(0);
    const [pageNumber, setPageNumber] = React.useState(1);
    const createToken = useCancelToken();

    const resetState = () => {
        cachedDmOrders = undefined;
        setData(cachedDmOrders);
        setTotalPages(0);
        setTotalRecords(0);
        setPageNumber(1);
        setError(undefined);
    };

    const fetch = async (pgNum = pageNumber) => {
        setError(undefined);
        setLoading(true);
        setPageNumber(pgNum);
        const cancelToken = createToken();
        try {
            await getJWTInfo(userId);
            const response = await getOrders({ pageNumber: pgNum, cancelToken });
            if (response.data) {
                cachedDmOrders = response.data?.Data?.Orders?.filter(it => !!it?.Status?.Name && it.Status.Name !== "Pending") || [];
                setData(cachedDmOrders);
                setTotalPages(response.data?.Data?.Paging?.TotalPages);
                setTotalRecords(response.data?.Data?.Paging?.TotalRecords);
            }
            if (response.error) setError(response.error);
        } catch (e) {
            if (axios.isCancel(error)) {
                console.log("Request canceled.", error.message);
                resetState();
            }
        } finally {
            setLoading(false);
        }
    };

    const onPageChange = async pgNum => {
        setPageNumber(pgNum);
        fetch(pgNum);
    };

    const reset = () => {
        resetState();
        setLoading(false);
    };

    React.useEffect(() => {
        if (enabled) fetch();
    }, [enabled]);

    const isPending = !!(isLoading && !data);

    return {
        fetch,
        data,
        isLoading,
        totalPages,
        totalRecords,
        setTotalPages,
        onPageChange,
        pageNumber,
        pageSize,
        error,
        isPending,
        reset
    };
}

let cachedShopOrders;
function useShopOrdersQuery({ enabled = true, userId } = {}) {
    const [data, setData] = React.useState(cachedShopOrders);
    const [error, setError] = React.useState(undefined);
    const [isLoading, setLoading] = React.useState(true);
    const [ordersPerPage, setOrdersPerPage] = React.useState(10);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [totalItems, setTotalItems] = React.useState(0);
    const createToken = useCancelToken();

    const ordersPerPageChange = e => {
        const selectValue = Number(e.currentTarget.value);
        setOrdersPerPage(selectValue);
        setCurrentPage(1);
        fetch(1, selectValue);
    };
    
    const onPageChange = pageNumber => {
        setCurrentPage(pageNumber);
        fetch(pageNumber, ordersPerPage);
    };

    const resetState = () => {
        cachedShopOrders = undefined;
        setData(cachedShopOrders);
        setCurrentPage(1);
        setError(undefined);
    };

    const fetch = async (pageNum = currentPage, limit = ordersPerPage) => {
        setLoading(true);
        setError(undefined);
        const cancelToken = createToken();
        const offset = (pageNum - 1) * limit;

        try {
            getJWTInfo(userId);
            const response = await getShopOrders({ offset, limit, cancelToken });
            if (response?.data?.orders) {
                cachedShopOrders = response.data.orders;
                setData(cachedShopOrders);
                if (response.data.pageInfo) setTotalItems(response.data.pageInfo.totalElements);
            }
            if (response.error) setError(response.error);
        } catch (e) {
            if (axios.isCancel(error)) {
                console.log("Request canceled.", error.message);
                resetState();
            }
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        resetState();
        setLoading(false);
    };

    React.useEffect(() => {
        if (enabled) fetch();
    }, [enabled]);

    const isPending = !!(isLoading && !data);

    return {
        data,
        totalItems,
        ordersPerPage,
        isLoading,
        currentPage,
        error,
        isPending,
        fetch,
        reset,
        ordersPerPageChange,
        onPageChange
    };
}

// cancel token management
function useCancelToken() {
    const cancelTokenSource = React.useRef(null);

    React.useEffect(() => {
        return () => {
            if (cancelTokenSource.current) {
                cancelTokenSource.current.cancel("Operation canceled due to component unmount.");
            }
        };
    }, []);

    const createToken = () => {
        if (cancelTokenSource.current) {
            cancelTokenSource.current.cancel("Operation canceled due to new request.");
        }
        cancelTokenSource.current = axios.CancelToken.source();
        return cancelTokenSource.current.token;
    };

    return createToken;
}
